import cn from 'classnames';
import PropTypes from 'prop-types';

import LdsValidationError from '../LdsValidationError/index.jsx';

export default function LdsCheckboxGroup({
  checked = null,
  children,
  className,
  error = false,
  errorMessage = '',
  indeterminate = null,
  label,
  onChange = () => {},
  required = false,
  selectAll = false,
  selectAllText = 'Select All',
  srRequiredText = 'This field is required',
  ariaControls = null,
  ...rest
}) {
  /*
    Checked state sets the aria-checked property.
    This is set up to follow this convention:
    https://www.w3.org/WAI/ARIA/apg/patterns/checkbox/examples/checkbox-mixed/

    State is determined by the following:
     - If all checkboxes are checked, checkedState is true
     - If no checkboxes are checked, checkedState is false
     - If some checkboxes are checked, checkedState is mixed

    User will be responsible for setting the 'indeterminate' and 'checked' props
  */
  let checkedState = 'false';
  if (checked) checkedState = 'true';
  if (indeterminate) checkedState = 'mixed';

  const handleSelectAllKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      onChange({ checked, indeterminate });
    }
  };

  return (
    <fieldset
      className={cn(
        'lds-checkbox-group',
        className
      )}
      aria-invalid={error ? 'true' : null}
      {...rest}
    >
      <legend>
        {label}
        {required && <span className="sr-only">{srRequiredText}</span>}
      </legend>

      {
        error && errorMessage
        && <LdsValidationError>{errorMessage}</LdsValidationError>
      }

      {
        selectAll
        && <div
          className={cn(
            'select-all',
            {
              'checked-state-checked': checkedState === 'true',
              'checked-state-unchecked': checkedState === 'false',
              'checked-state-mixed': checkedState === 'mixed',
            }
          )}
          role="checkbox"
          aria-checked={checkedState}
          tabIndex="0"
          onClick={() => onChange({ checked, indeterminate })}
          aria-controls={ariaControls}
          onKeyDown={handleSelectAllKeyDown}
        >
          <div className="box" aria-hidden="true" />
          {selectAllText}
        </div>
      }
      {children}
    </fieldset>
  );
}

LdsCheckboxGroup.propTypes = {
  checked: (props, propName, componentName) => { // eslint-disable-line
    if (props.selectAll === true && (props[propName] === null || props[propName] === undefined)) {
      return new Error(`LDS ERROR >> Invalid prop "${propName}" supplied to ${componentName}.
        Prop "${propName}" is a required boolean when "selectAll" is used. This prop is used to show the state of the select all box.
      `);
    }
  },
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  indeterminate: PropTypes.bool,
  onChange: (props, propName, componentName) => { // eslint-disable-line
    if (props.selectAll === true && (!props[propName] || typeof (props[propName]) !== 'function')) {
      return new Error(`LDS ERROR >> Invalid prop "${propName}" supplied to ${componentName}.
        Prop "${propName}" is a required function when "selectAll" is used. This prop is used to set the state of the select all box when clicked.
      `);
    }
  },
  required: PropTypes.bool,
  selectAll: PropTypes.bool,
  selectAllText: PropTypes.string,
  srRequiredText: PropTypes.string,
  ariaControls: (props, propName, componentName) => { // eslint-disable-line
    if (props.selectAll === true && !props[propName]) {
      return new Error(`LDS ERROR >> Invalid prop "${propName}" supplied to ${componentName}.
        Prop "${propName}" is a required string when "selectAll" is used. Pass a string of space separated ids of the checkboxes in the group.
      `);
    }
  },
};
